<script setup lang="ts">
import { computed } from 'vue';
import {
  formatDistance,
  formatDuration,
  googleMapsUrl,
  doorOf,
  maneuverRotation,
  originLngLat,
  type Origin,
  type Route,
  type TravelMode,
} from '@/data/directions';
import type { SearchIndexEntry } from '@/data/search';
import type { MapLocation } from '@/data/types';
import PanelHeading from './PanelHeading.vue';
import SearchBox from './SearchBox.vue';

const props = defineProps<{
  destination: MapLocation;
  origin: Origin | null;
  mode: TravelMode;
  /** Walking to the step-free entrance. */
  accessible: boolean;
  route: Route | null;
  routeStatus: 'idle' | 'loading' | 'ready' | 'error';
  routeError: string | null;
  searchIndex: SearchIndexEntry[];
}>();

const emit = defineEmits<{
  back: [];
  pickOrigin: [slug: string];
  useMyLocation: [];
  clearOrigin: [];
  swap: [];
  setMode: [mode: TravelMode];
  setAccessible: [on: boolean];
}>();

const modes: { mode: TravelMode; label: string }[] = [
  { mode: 'walking', label: 'Walk' },
  { mode: 'driving', label: 'Drive' },
];

// A failed "my location" falls back to the picker (with the error shown below it).
const showPicker = computed(() => !props.origin || (props.origin.kind === 'here' && props.origin.status === 'error'));

const originLabel = computed(() => {
  const o = props.origin;
  if (!o) return '';
  if (o.kind === 'location') return o.location.title;
  return o.status === 'locating' ? 'Finding your location…' : 'Your location';
});

const fallbackUrl = computed(() =>
  googleMapsUrl(props.mode, doorOf(props.destination, props.accessible), originLngLat(props.origin) ?? undefined),
);

// ~2 miles: past this, walking is probably not what someone wants.
const LONG_WALK_METERS = 3200;
const longWalk = computed(
  () => props.mode === 'walking' && !!props.route && props.route.distance > LONG_WALK_METERS,
);

const summary = computed(() =>
  props.route ? `${formatDuration(props.route.duration)} · ${formatDistance(props.route.distance)}` : '',
);
</script>

<template>
  <section class="dir">
    <PanelHeading title="Directions" :back-label="`Back to ${destination.title}`" @back="emit('back')" />

    <div class="dir__ends">
      <div class="dir__rail" aria-hidden="true">
        <span class="dot dot--from" />
        <span class="rail" />
        <span class="dot dot--to" />
      </div>

      <div class="dir__fields">
        <div class="dir__field">
          <span class="dir__label">From</span>
          <div v-if="origin && !showPicker" class="dir__value">
            <span class="dir__name" :class="{ 'is-muted': origin.kind === 'here' && origin.status === 'locating' }">
              {{ originLabel }}
            </span>
            <button type="button" class="link-btn" @click="emit('clearOrigin')">Change</button>
          </div>
          <div v-else class="dir__picker">
            <button type="button" class="here-btn" @click="emit('useMyLocation')">
              <svg viewBox="0 0 512 512" width="14" height="14" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M429.6 92.1c4.9-11.9 2.1-25.6-7-34.7s-22.8-11.9-34.7-7l-352 144c-14.2 5.8-22.2 20.8-19.3 35.8S32.7 256 48 256h176v176c0 15.3 10.8 28.4 25.8 31.4s30-5.1 35.8-19.3l144-352z"
                />
              </svg>
              Use my location
            </button>
            <SearchBox
              :index="searchIndex"
              input-id="origin-search"
              label="Starting point"
              placeholder="Or search for a starting point"
              :exclude="destination.slug"
              @select="(slug) => emit('pickOrigin', slug)"
            />
          </div>
        </div>

        <div class="dir__field">
          <span class="dir__label">To</span>
          <div class="dir__value">
            <span class="dir__name">{{ destination.title }}</span>
          </div>
        </div>
      </div>

      <button
        v-if="origin?.kind === 'location'"
        type="button"
        class="swap-btn"
        aria-label="Swap start and destination"
        title="Swap"
        @click="emit('swap')"
      >
        <svg viewBox="0 0 448 512" width="16" height="16" aria-hidden="true">
          <path
            fill="currentColor"
            d="M438.6 150.6a32 32 0 0 0 0-45.3l-96-96a32 32 0 0 0-45.3 45.3L338.7 96H32a32 32 0 0 0 0 64h306.7l-41.4 41.4a32 32 0 0 0 45.3 45.3l96-96zm-333.3 352a32 32 0 0 0 45.3-45.3L109.3 416H416a32 32 0 0 0 0-64H109.3l41.4-41.4a32 32 0 0 0-45.3-45.3l-96 96a32 32 0 0 0 0 45.3l96 96z"
          />
        </svg>
      </button>
    </div>

    <div class="modes" role="radiogroup" aria-label="Travel mode">
      <button
        v-for="m in modes"
        :key="m.mode"
        type="button"
        role="radio"
        class="mode"
        :class="{ 'is-selected': mode === m.mode }"
        :aria-checked="mode === m.mode"
        @click="emit('setMode', m.mode)"
      >
        {{ m.label }}
      </button>
    </div>

    <label v-if="mode === 'walking' && destination.accessibleEntrance" class="acc">
      <input
        type="checkbox"
        :checked="accessible"
        @change="emit('setAccessible', ($event.target as HTMLInputElement).checked)"
      />
      <span>Go to the step-free entrance</span>
    </label>
    <p v-if="mode === 'walking' && destination.accessibilityNotes" class="acc-notes">
      {{ destination.accessibilityNotes }}
    </p>

    <p v-if="origin?.kind === 'here' && origin.status === 'error'" class="dir__error" role="alert">
      {{ origin.message }}
    </p>

    <div aria-live="polite">
      <p v-if="routeStatus === 'loading'" class="dir__status">Finding a route…</p>
      <p v-else-if="routeStatus === 'error'" class="dir__error" role="alert">{{ routeError }}</p>
      <p v-else-if="route" class="dir__summary">
        <strong>{{ formatDuration(route.duration) }}</strong>
        <span>{{ formatDistance(route.distance) }} {{ route.mode === 'walking' ? 'walk' : 'drive' }}</span>
        <span class="visually-hidden">Route found: {{ summary }}</span>
      </p>
      <p v-if="longWalk && routeStatus === 'ready'" class="long-walk">
        That’s a long walk.
        <button type="button" class="link-btn" @click="emit('setMode', 'driving')">Get driving directions</button>
      </p>
    </div>

    <ol v-if="route && routeStatus === 'ready'" class="steps">
      <li v-for="(step, i) in route.steps" :key="i" class="step">
        <span class="step__icon" aria-hidden="true">
          <svg
            v-if="step.type === 'arrive'"
            viewBox="0 0 384 512"
            width="14"
            height="14"
          >
            <path
              fill="currentColor"
              d="M215.7 499.2C267 435 384 279.4 384 192 384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2a30.8 30.8 0 0 0 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"
            />
          </svg>
          <svg v-else viewBox="0 0 384 512" width="14" height="14" :style="{ rotate: `${maneuverRotation(step.modifier)}deg` }">
            <path
              fill="currentColor"
              d="M214.6 41.4a32 32 0 0 0-45.3 0l-160 160a32 32 0 0 0 45.3 45.3L160 141.2V448a32 32 0 1 0 64 0V141.2l105.4 105.4a32 32 0 0 0 45.3-45.3l-160-160z"
            />
          </svg>
        </span>
        <span class="step__text">{{ step.instruction }}</span>
        <span v-if="step.distance > 0" class="step__dist">{{ formatDistance(step.distance) }}</span>
      </li>
    </ol>

    <p class="dir__footer">
      <a :href="fallbackUrl" target="_blank" rel="noopener">
        Open in Google Maps<span class="visually-hidden"> (new tab)</span>
      </a>
      <span>Routes by Mapbox. Check signs and accessible entrances on arrival.</span>
    </p>
  </section>
</template>

<style scoped>
.dir__ends {
  position: relative;
  display: flex;
  gap: 10px;
  padding: 10px 10px 10px 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.dir__rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 26px 0 16px;
}

.dot {
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot--from {
  border: 3px solid var(--interactive-blue);
  background: #fff;
}

.dot--to {
  background: var(--ur-blue);
}

.rail {
  flex: 1;
  width: 2px;
  margin: 4px 0;
  background: repeating-linear-gradient(to bottom, var(--border) 0 4px, transparent 4px 8px);
}

.dir__fields {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dir__label {
  display: block;
  margin-bottom: 2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.dir__value {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding-right: 36px; /* room for the swap button */
}

.dir__name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}

.dir__name.is-muted {
  color: var(--text-muted);
}

.dir__picker {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.link-btn {
  padding: 0;
  border: 0;
  background: none;
  color: var(--interactive-blue);
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
}

.here-btn {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--interactive-blue);
  border-radius: 999px;
  background: var(--surface);
  color: var(--interactive-blue);
  font-weight: 700;
  font-size: 14px;
}

.here-btn:hover {
  background: var(--surface-alt);
}

.swap-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  translate: 0 -50%;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: var(--surface);
  color: var(--interactive-blue);
  rotate: 90deg;
}

.swap-btn:hover {
  background: var(--surface-alt);
}

.modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-top: 12px;
  border: 1px solid var(--interactive-blue);
  border-radius: var(--radius);
  overflow: hidden;
}

.mode {
  height: 36px;
  border: 0;
  background: var(--surface);
  color: var(--interactive-blue);
  font-weight: 700;
  font-size: 14px;
}

.mode.is-selected {
  background: var(--interactive-blue);
  color: #fff;
}

.acc {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.acc input {
  width: 18px;
  height: 18px;
  accent-color: var(--interactive-blue);
}

.acc-notes {
  margin: 6px 0 0 26px;
  font-size: 13px;
  color: var(--text-muted);
}

.long-walk {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 4px 0 0;
  padding: 8px 10px;
  border-radius: var(--radius);
  background: #fff6d6;
  font-size: 14px;
}

.dir__status,
.dir__error {
  margin: 12px 0 0;
  font-size: 14px;
}

.dir__status {
  color: var(--text-muted);
}

.dir__error {
  color: var(--danger);
}

.dir__summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 14px 0 4px;
}

.dir__summary strong {
  font-size: 22px;
  color: var(--ur-blue);
}

.dir__summary span {
  color: var(--text-muted);
}

.steps {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.step {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  align-items: start;
  gap: 8px;
  padding: 9px 0;
  border-bottom: 1px solid var(--border);
  font-size: 14px;
  line-height: 1.35;
}

.step:last-child {
  border-bottom: 0;
}

.step__icon {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--surface-alt);
  color: var(--interactive-blue);
}

.step__dist {
  color: var(--text-muted);
  font-size: 13px;
  white-space: nowrap;
}

.dir__footer {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 14px 0 0;
  font-size: 12px;
  color: var(--text-muted);
}

.dir__footer a {
  color: var(--interactive-blue);
  font-weight: 700;
  font-size: 13px;
}
</style>
